import React, { useEffect } from 'react';
import DashboardCard from 'components/DashboardCard/DashboardCard';
import { Add, Description } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import { actions } from './Reports.redux';
import ModalReportAddNew from './ModalReportAddNew';
import ReportsCell from './ReportsCell';
import NumberedDataGridPagination from 'components/NumberedDataGridPagination';
import { useNavigate } from '@tanstack/react-router';

const ReportsList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const loading = useSelector((state) => state.reports.loading);
    const { rows, totalRows, lastQuery, pageSize, pageNumber } = useSelector((state) => state.reports.reports);
    const showNewReport = useSelector((state) => state.reports.showNewReportModal);

    const columns = [
        { field: 'urpt_name', headerName: 'Report Title', flex: 1, minWidth: 180, sortable: false },
        { field: 'date_created', headerName: 'Report Date', flex: 1, minWidth: 160, sortable: false },
        { field: 'patient_name', headerName: 'Patient', flex: 1, minWidth: 180, sortable: false },
        { field: 'client_name', headerName: 'Client', flex: 1, minWidth: 160, sortable: false },
        { field: 'visit_date', headerName: 'Visit Date', flex: 1, minWidth: 150, sortable: false },
        { field: 'template_name', headerName: 'Report Template', flex: 1, minWidth: 200, sortable: false },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => <ReportsCell {...params} />,
        },
    ];

    const handlePaginationModelChange = (newModel) => {
        if (loading) {
            return;
        }

        dispatch(
            actions.setSubState('reports', {
                pageNumber: newModel.page,
                pageSize: newModel.pageSize,
            })
        );
        dispatch(actions.loadReports());
    };

    useEffect(() => {
        if (lastQuery === '' && loading === false) {
            dispatch(actions.loadReports());
        }
    }, [lastQuery, loading, dispatch]);

    return (
        <DashboardCard
            color={'amber'}
            icon={<Description />}
            topContent={'Reports'}
            topLabel={
                <div>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => dispatch(actions.setState({ showNewReportModal: true }))}
                    >
                        Add New
                    </Button>
                </div>
            }
            size="xl"
        >
            <div style={{ width: '100%' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.urpt_id}
                    rowCount={Number(totalRows) || 0}
                    pagination
                    paginationMode="server"
                    pageSizeOptions={[5, 10, 20, 30]}
                    paginationModel={{ page: pageNumber, pageSize }}
                    onPaginationModelChange={handlePaginationModelChange}
                    loading={loading}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    disableCellFocus
                    sx={{
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                            outline: 'none',
                        },
                    }}
                    slots={{ pagination: NumberedDataGridPagination }}
                    onRowClick={(params) => {
                        if (params.field !== 'actions') {
                            navigate({ to: `/reports/edit/${params.row.urpt_id}` });
                        }
                    }}
                    autoHeight
                />
            </div>

            {showNewReport && <ModalReportAddNew />}
        </DashboardCard>
    );
};

export default ReportsList;
